import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiAlertCircle,
    FiChevronLeft,
    FiChevronRight,
    FiCreditCard,
    FiFileText,
    FiLoader,
    FiPrinter,
    FiRefreshCw,
    FiSearch,
    FiTrendingDown,
    FiTrendingUp,
    FiUser,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    customerLedgerEntriesSelector,
    customerLedgerErrorSelector,
    customerLedgerFiltersSelector,
    customerLedgerLoadingSelector,
    customerLedgerPaginationSelector,
    customerLedgerSummarySelector,
    fetchCustomerLedger,
    setCustomerLedgerFilters,
    setCustomerLedgerLimit,
    setCustomerLedgerPage,
} from "../../../../../../../store/billing/customerLedgerSlice";

import "./CustomerLedgerStatement.css";

/*
|--------------------------------------------------------------------------
| CONSTANTS
|--------------------------------------------------------------------------
*/

const STATUS_OPTIONS = [
    {
        value: "",
        label: "All Entries",
    },
    {
        value: "ACTIVE",
        label: "Active",
    },
    {
        value: "REVERSED",
        label: "Reversed",
    },
    {
        value: "CANCELLED",
        label: "Cancelled",
    },
];

const PAGE_SIZE_OPTIONS = [
    20,
    50,
    100,
    200,
];

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const parsedValue =
        Number(value);

    return Number.isFinite(
        parsedValue
    )
        ? parsedValue
        : fallback;
};

const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(value) +
            Number.EPSILON
        ) *
        100
    ) / 100;

const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    ).format(
        roundMoney(value)
    );

const formatDate = (
    value,
    includeTime = false
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        includeTime
            ? {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            }
            : {
                day: "2-digit",
                month: "short",
                year: "numeric",
            }
    ).format(date);
};

const formatLabel = (
    value
) => {
    if (!value) {
        return "-";
    }

    return String(value)
        .replace(
            /_/g,
            " "
        )
        .toLowerCase()
        .replace(
            /\b\w/g,
            (
                character
            ) =>
                character.toUpperCase()
        );
};

const getInvoiceCustomerId = (
    invoice
) =>
    invoice?.customer?._id ||
    invoice?.customer ||
    invoice?.customerId ||
    invoice?.customerSnapshot?.customerId ||
    "";

const getInvoiceWebsiteUserId = (
    invoice
) =>
    invoice?.websiteUser?._id ||
    invoice?.websiteUser ||
    invoice?.websiteUserId ||
    "";

const getInvoiceCustomerMobile = (
    invoice
) =>
    invoice?.customerSnapshot?.mobile ||
    invoice?.customer?.mobile ||
    invoice?.customerMobile ||
    invoice?.mobile ||
    "";


const getInvoiceId = (
    invoice
) =>
    invoice?._id ||
    invoice?.id ||
    "";

const getCustomerName = (
    invoice,
    entries = []
) =>
    invoice
        ?.customerSnapshot
        ?.customerName ||
    invoice
        ?.customerSnapshot
        ?.name ||
    invoice
        ?.customer
        ?.customerName ||
    invoice
        ?.customer
        ?.name ||
    entries[0]
        ?.customer
        ?.customerName ||
    "Walk-in Customer";

const getCustomerMobile = (
    invoice,
    entries = []
) =>
    getInvoiceCustomerMobile(
        invoice
    ) ||
    entries[0]
        ?.customer
        ?.mobile ||
    "-";

const getCustomerEmail = (
    invoice,
    entries = []
) =>
    invoice
        ?.customerSnapshot
        ?.email ||
    invoice
        ?.customer
        ?.email ||
    entries[0]
        ?.customer
        ?.email ||
    "-";

const getDocumentNumber = (
    entry
) =>
    entry
        ?.document
        ?.documentNumber ||
    entry
        ?.documentNumber ||
    "-";

const getReceiptNumber = (
    entry
) =>
    entry
        ?.payment
        ?.receiptNumber ||
    entry
        ?.receiptNumber ||
    "-";

const getReferenceText = (
    entry
) => {
    const transactionType =
        String(
            entry
                ?.transactionType ||
            ""
        ).toUpperCase();

    if (
        transactionType ===
        "SALE_INVOICE"
    ) {
        return getDocumentNumber(
            entry
        );
    }

    if (
        [
            "PAYMENT_IN",
            "PAYMENT_REVERSAL",
        ].includes(
            transactionType
        )
    ) {
        return getReceiptNumber(
            entry
        );
    }

    return (
        entry
            ?.referenceNumber ||
        entry
            ?.document
            ?.documentNumber ||
        entry
            ?.payment
            ?.receiptNumber ||
        "-"
    );
};

const getEntryTypeClass = (
    entry
) => {
    const transactionType =
        String(
            entry
                ?.transactionType ||
            ""
        ).toLowerCase();

    return `customer-ledger-type-${transactionType}`;
};

const getStatusClass = (
    status
) =>
    `customer-ledger-status-${String(
        status ||
        "ACTIVE"
    ).toLowerCase()}`;

const calculatePageOpeningBalance = (
    entries
) => {
    if (
        !Array.isArray(
            entries
        ) ||
        entries.length ===
        0
    ) {
        return 0;
    }

    return roundMoney(
        entries[0]
            ?.openingBalance ||
        0
    );
};

const calculatePageClosingBalance = (
    entries
) => {
    if (
        !Array.isArray(
            entries
        ) ||
        entries.length ===
        0
    ) {
        return 0;
    }

    return roundMoney(
        entries[
            entries.length -
            1
        ]?.closingBalance ||
        entries[
            entries.length -
            1
        ]?.runningBalance ||
        0
    );
};

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const CustomerLedgerStatement =
    ({
        invoice,
        onInvoiceClick,
        onPaymentClick,
    }) => {
        const dispatch =
            useDispatch();

        const entries =
            useSelector(
                customerLedgerEntriesSelector
            ) || [];

        const summary =
            useSelector(
                customerLedgerSummarySelector
            ) || {};

        const pagination =
            useSelector(
                customerLedgerPaginationSelector
            ) || {};

        const savedFilters =
            useSelector(
                customerLedgerFiltersSelector
            ) || {};

        const loading =
            useSelector(
                customerLedgerLoadingSelector
            );

        const error =
            useSelector(
                customerLedgerErrorSelector
            ) || "";

        const [
            localFilters,
            setLocalFilters,
        ] = useState({
            fromDate:
                savedFilters
                    ?.fromDate ||
                "",

            toDate:
                savedFilters
                    ?.toDate ||
                "",

            status:
                savedFilters
                    ?.status ||
                "",
        });

        const customerId =
            getInvoiceCustomerId(
                invoice
            );

        const websiteUserId =
            getInvoiceWebsiteUserId(
                invoice
            );

        const mobile =
            getInvoiceCustomerMobile(
                invoice
            );

        const invoiceId =
            getInvoiceId(
                invoice
            );

        const isInvoiceLedgerFallback =
            Boolean(
                !customerId &&
                !websiteUserId &&
                !mobile &&
                invoiceId
            );

        const page =
            Math.max(
                Number(
                    pagination
                        ?.page
                ) || 1,
                1
            );

        const limit =
            Math.max(
                Number(
                    pagination
                        ?.limit
                ) || 50,
                1
            );

        const total =
            Math.max(
                Number(
                    pagination
                        ?.total
                ) || 0,
                0
            );

        const totalPages =
            Math.max(
                Number(
                    pagination
                        ?.totalPages
                ) || 1,
                1
            );

        const canLoadLedger =
            Boolean(
                customerId ||
                websiteUserId ||
                mobile ||
                invoiceId
            );

        /*
        |--------------------------------------------------------------------------
        | FETCH LEDGER
        |--------------------------------------------------------------------------
        */

        const loadLedger =
            useCallback(
                async ({
                    requestedPage =
                    page,

                    requestedLimit =
                    limit,

                    requestedFilters =
                    localFilters,
                } = {}) => {
                    if (
                        !canLoadLedger
                    ) {
                        return null;
                    }

                    return dispatch(
                        fetchCustomerLedger({
                            customerId,
                            websiteUserId:
                                customerId
                                    ? ""
                                    : websiteUserId,
                            mobile:
                                customerId ||
                                    websiteUserId
                                    ? ""
                                    : mobile,
                            invoiceId:
                                customerId ||
                                    websiteUserId ||
                                    mobile
                                    ? ""
                                    : invoiceId,
                            fromDate:
                                requestedFilters
                                    ?.fromDate ||
                                "",
                            toDate:
                                requestedFilters
                                    ?.toDate ||
                                "",
                            status:
                                requestedFilters
                                    ?.status ||
                                "",
                            page:
                                requestedPage,
                            limit:
                                requestedLimit,
                        })
                    );
                },
                [
                    canLoadLedger,
                    customerId,
                    dispatch,
                    limit,
                    localFilters,
                    mobile,
                    invoiceId,
                    page,
                    websiteUserId,
                ]
            );
        useEffect(() => {
            if (
                !customerId &&
                !websiteUserId &&
                !mobile &&
                !invoiceId
            ) {
                console.warn(
                    "Customer ledger API not called because customer identifier is missing.",
                    {
                        customerId,
                        websiteUserId,
                        mobile,
                    }
                );

                return;
            }

            dispatch(
                fetchCustomerLedger({
                    customerId,

                    websiteUserId:
                        customerId
                            ? ""
                            : websiteUserId,

                    mobile:
                        customerId ||
                            websiteUserId
                            ? ""
                            : mobile,

                    invoiceId:
                        customerId ||
                            websiteUserId ||
                            mobile
                            ? ""
                            : invoiceId,

                    fromDate:
                        localFilters.fromDate ||
                        "",

                    toDate:
                        localFilters.toDate ||
                        "",

                    status:
                        localFilters.status ||
                        "",

                    page,

                    limit,
                })
            );
        }, [
            dispatch,
            customerId,
            websiteUserId,
            mobile,
            invoiceId,
            page,
            limit,
        ]);
        useEffect(() => {
            if (
                !canLoadLedger
            ) {
                return;
            }

            loadLedger();
        }, [
            canLoadLedger,
            loadLedger,
        ]);

        /*
        |--------------------------------------------------------------------------
        | DERIVED VALUES
        |--------------------------------------------------------------------------
        */

        const pageOpeningBalance =
            useMemo(
                () =>
                    isInvoiceLedgerFallback
                        ? 0
                        : calculatePageOpeningBalance(
                            entries
                        ),
                [
                    entries,
                    isInvoiceLedgerFallback,
                ]
            );

        const pageClosingBalance =
            useMemo(
                () =>
                    isInvoiceLedgerFallback
                        ? roundMoney(
                            summary?.balance
                        )
                        : calculatePageClosingBalance(
                            entries
                        ),
                [
                    entries,
                    isInvoiceLedgerFallback,
                    summary?.balance,
                ]
            );

        const customerName =
            getCustomerName(
                invoice,
                entries
            );

        const customerMobile =
            getCustomerMobile(
                invoice,
                entries
            );

        const customerEmail =
            getCustomerEmail(
                invoice,
                entries
            );

        /*
        |--------------------------------------------------------------------------
        | FILTER ACTIONS
        |--------------------------------------------------------------------------
        */

        const handleFilterChange =
            (
                field,
                value
            ) => {
                setLocalFilters(
                    (
                        current
                    ) => ({
                        ...current,
                        [field]:
                            value,
                    })
                );
            };

        const handleApplyFilters =
            async () => {
                dispatch(
                    setCustomerLedgerFilters({
                        fromDate:
                            localFilters
                                .fromDate,
                        toDate:
                            localFilters
                                .toDate,
                        status:
                            localFilters
                                .status,
                    })
                );

                dispatch(
                    setCustomerLedgerPage(
                        1
                    )
                );

                await loadLedger({
                    requestedPage:
                        1,
                    requestedFilters:
                        localFilters,
                });
            };

        const handleClearFilters =
            async () => {
                const clearedFilters = {
                    fromDate: "",
                    toDate: "",
                    status: "",
                };

                setLocalFilters(
                    clearedFilters
                );

                dispatch(
                    setCustomerLedgerFilters(
                        clearedFilters
                    )
                );

                dispatch(
                    setCustomerLedgerPage(
                        1
                    )
                );

                await loadLedger({
                    requestedPage:
                        1,
                    requestedFilters:
                        clearedFilters,
                });
            };

        /*
        |--------------------------------------------------------------------------
        | PAGINATION
        |--------------------------------------------------------------------------
        */

        const handlePreviousPage =
            () => {
                if (
                    loading ||
                    page <= 1
                ) {
                    return;
                }

                dispatch(
                    setCustomerLedgerPage(
                        page - 1
                    )
                );
            };

        const handleNextPage =
            () => {
                if (
                    loading ||
                    page >=
                    totalPages
                ) {
                    return;
                }

                dispatch(
                    setCustomerLedgerPage(
                        page + 1
                    )
                );
            };

        const handleLimitChange =
            (
                event
            ) => {
                dispatch(
                    setCustomerLedgerLimit(
                        Number(
                            event
                                .target
                                .value
                        )
                    )
                );
            };

        /*
        |--------------------------------------------------------------------------
        | ROW CLICK
        |--------------------------------------------------------------------------
        */

        const handleReferenceClick =
            (
                entry
            ) => {
                const transactionType =
                    String(
                        entry
                            ?.transactionType ||
                        ""
                    ).toUpperCase();

                if (
                    transactionType ===
                    "SALE_INVOICE" &&
                    typeof onInvoiceClick ===
                    "function"
                ) {
                    onInvoiceClick(
                        entry
                    );

                    return;
                }

                if (
                    [
                        "PAYMENT_IN",
                        "PAYMENT_REVERSAL",
                    ].includes(
                        transactionType
                    ) &&
                    typeof onPaymentClick ===
                    "function"
                ) {
                    onPaymentClick(
                        entry
                    );
                }
            };

        /*
        |--------------------------------------------------------------------------
        | PRINT
        |--------------------------------------------------------------------------
        */

        const handlePrint =
            () => {
                window.print();
            };

        if (
            !canLoadLedger
        ) {
            return (
                <section className="customer-ledger-statement">
                    <div className="customer-ledger-empty-state">
                        <FiUser />

                        <strong>
                            Customer ledger unavailable
                        </strong>

                        <span>
                            This invoice does not contain a customer identifier or invoice reference that can be used for ledger lookup.
                        </span>
                    </div>
                </section>
            );
        }

        return (
            <section className="customer-ledger-statement">
                <header className="customer-ledger-header">
                    <div className="customer-ledger-heading">
                        <span>
                            <FiFileText />
                        </span>

                        <div>
                            <h3>
                                Customer Ledger
                            </h3>

                            <p>
                                {isInvoiceLedgerFallback
                                    ? "Invoice-specific debit, payment and credit-note history for this walk-in customer."
                                    : "Complete debit, credit and outstanding history for this customer."}
                            </p>
                        </div>
                    </div>

                    <div className="customer-ledger-header-actions">
                        <button
                            type="button"
                            className="customer-ledger-refresh"
                            disabled={
                                loading
                            }
                            onClick={() =>
                                loadLedger()
                            }
                        >
                            <FiRefreshCw
                                className={
                                    loading
                                        ? "customer-ledger-spin"
                                        : ""
                                }
                            />

                            Refresh
                        </button>

                        <button
                            type="button"
                            className="customer-ledger-print"
                            disabled={
                                loading ||
                                entries.length ===
                                0
                            }
                            onClick={
                                handlePrint
                            }
                        >
                            <FiPrinter />

                            Print Statement
                        </button>
                    </div>
                </header>

                <section className="customer-ledger-customer-card">
                    <div className="customer-ledger-customer-icon">
                        <FiUser />
                    </div>

                    <div className="customer-ledger-customer-info">
                        <strong>
                            {customerName}
                        </strong>

                        <span>
                            {customerMobile}
                        </span>

                        <span>
                            {customerEmail}
                        </span>
                    </div>

                    <div className="customer-ledger-customer-balance">
                        <span>
                            Current Outstanding
                        </span>

                        <strong
                            className={
                                roundMoney(
                                    summary
                                        ?.balance
                                ) >
                                    0
                                    ? "customer-ledger-balance-due"
                                    : "customer-ledger-balance-clear"
                            }
                        >
                            {formatCurrency(
                                summary
                                    ?.balance
                            )}
                        </strong>
                    </div>
                </section>

                <section className="customer-ledger-filters">
                    <div className="customer-ledger-filter-field">
                        <label>
                            From Date
                        </label>

                        <input
                            type="date"
                            value={
                                localFilters
                                    .fromDate
                            }
                            disabled={
                                loading
                            }
                            onChange={(
                                event
                            ) =>
                                handleFilterChange(
                                    "fromDate",
                                    event
                                        .target
                                        .value
                                )
                            }
                        />
                    </div>

                    <div className="customer-ledger-filter-field">
                        <label>
                            To Date
                        </label>

                        <input
                            type="date"
                            value={
                                localFilters
                                    .toDate
                            }
                            disabled={
                                loading
                            }
                            onChange={(
                                event
                            ) =>
                                handleFilterChange(
                                    "toDate",
                                    event
                                        .target
                                        .value
                                )
                            }
                        />
                    </div>

                    <div className="customer-ledger-filter-field">
                        <label>
                            Status
                        </label>

                        <select
                            value={
                                localFilters
                                    .status
                            }
                            disabled={
                                loading
                            }
                            onChange={(
                                event
                            ) =>
                                handleFilterChange(
                                    "status",
                                    event
                                        .target
                                        .value
                                )
                            }
                        >
                            {STATUS_OPTIONS.map(
                                (
                                    option
                                ) => (
                                    <option
                                        key={
                                            option.value ||
                                            "ALL"
                                        }
                                        value={
                                            option.value
                                        }
                                    >
                                        {option.label}
                                    </option>
                                )
                            )}
                        </select>
                    </div>

                    <div className="customer-ledger-filter-actions">
                        <button
                            type="button"
                            className="customer-ledger-clear-filter"
                            disabled={
                                loading
                            }
                            onClick={
                                handleClearFilters
                            }
                        >
                            Clear
                        </button>

                        <button
                            type="button"
                            className="customer-ledger-apply-filter"
                            disabled={
                                loading
                            }
                            onClick={
                                handleApplyFilters
                            }
                        >
                            <FiSearch />

                            Apply
                        </button>
                    </div>
                </section>

                <section className="customer-ledger-summary">
                    <div>
                        <span>
                            Opening Balance
                        </span>

                        <strong>
                            {formatCurrency(
                                pageOpeningBalance
                            )}
                        </strong>
                    </div>

                    <div className="customer-ledger-summary-debit">
                        <span>
                            Total Debit
                        </span>

                        <strong>
                            <FiTrendingUp />

                            {formatCurrency(
                                summary
                                    ?.totalDebit
                            )}
                        </strong>
                    </div>

                    <div className="customer-ledger-summary-credit">
                        <span>
                            Total Credit
                        </span>

                        <strong>
                            <FiTrendingDown />

                            {formatCurrency(
                                summary
                                    ?.totalCredit
                            )}
                        </strong>
                    </div>

                    <div className="customer-ledger-summary-closing">
                        <span>
                            Closing Balance
                        </span>

                        <strong>
                            {formatCurrency(
                                pageClosingBalance ||
                                summary
                                    ?.balance
                            )}
                        </strong>
                    </div>
                </section>

                {error && (
                    <div className="customer-ledger-error">
                        <FiAlertCircle />

                        <span>
                            {error}
                        </span>

                        <button
                            type="button"
                            disabled={
                                loading
                            }
                            onClick={() =>
                                loadLedger()
                            }
                        >
                            Retry
                        </button>
                    </div>
                )}

                <div className="customer-ledger-table-wrap">
                    <table className="customer-ledger-table">
                        <thead>
                            <tr>
                                <th>
                                    Date
                                </th>

                                <th>
                                    Particulars
                                </th>

                                <th>
                                    Reference
                                </th>

                                <th>
                                    Status
                                </th>

                                <th className="customer-ledger-text-right">
                                    Debit
                                </th>

                                <th className="customer-ledger-text-right">
                                    Credit
                                </th>

                                <th className="customer-ledger-text-right">
                                    Balance
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {loading &&
                                entries.length ===
                                0 ? (
                                <tr>
                                    <td
                                        colSpan="7"
                                        className="customer-ledger-loading"
                                    >
                                        <FiLoader className="customer-ledger-spin" />

                                        Loading customer ledger...
                                    </td>
                                </tr>
                            ) : entries.length ===
                                0 ? (
                                <tr>
                                    <td
                                        colSpan="7"
                                        className="customer-ledger-empty"
                                    >
                                        <FiFileText />

                                        <strong>
                                            No ledger entries found
                                        </strong>

                                        <span>
                                            Confirmed invoices and received payments will appear here.
                                        </span>
                                    </td>
                                </tr>
                            ) : (
                                entries.map(
                                    (
                                        entry,
                                        index
                                    ) => (
                                        <tr
                                            key={
                                                entry
                                                    ?._id ||
                                                index
                                            }
                                            className={
                                                entry
                                                    ?.isReversal
                                                    ? "customer-ledger-reversal-row"
                                                    : ""
                                            }
                                        >
                                            <td>
                                                <strong>
                                                    {formatDate(
                                                        entry
                                                            ?.transactionDate
                                                    )}
                                                </strong>

                                                <small>
                                                    {formatDate(
                                                        entry
                                                            ?.createdAt,
                                                        true
                                                    )}
                                                </small>
                                            </td>

                                            <td>
                                                <span
                                                    className={`customer-ledger-type ${getEntryTypeClass(
                                                        entry
                                                    )}`}
                                                >
                                                    {entry
                                                        ?.direction ===
                                                        "DEBIT" ? (
                                                        <FiTrendingUp />
                                                    ) : (
                                                        <FiTrendingDown />
                                                    )}

                                                    {formatLabel(
                                                        entry
                                                            ?.transactionType
                                                    )}
                                                </span>

                                                <strong className="customer-ledger-narration">
                                                    {entry
                                                        ?.narration ||
                                                        "-"}
                                                </strong>

                                                {entry
                                                    ?.reversalReason && (
                                                        <small className="customer-ledger-reversal-reason">
                                                            Reason:{" "}
                                                            {
                                                                entry.reversalReason
                                                            }
                                                        </small>
                                                    )}
                                            </td>

                                            <td>
                                                <button
                                                    type="button"
                                                    className="customer-ledger-reference-button"
                                                    disabled={
                                                        String(
                                                            entry
                                                                ?.transactionType ||
                                                            ""
                                                        ).toUpperCase() ===
                                                            "SALE_INVOICE"
                                                            ? typeof onInvoiceClick !==
                                                            "function"
                                                            : typeof onPaymentClick !==
                                                            "function"
                                                    }
                                                    onClick={() =>
                                                        handleReferenceClick(
                                                            entry
                                                        )
                                                    }
                                                >
                                                    {String(
                                                        entry
                                                            ?.transactionType ||
                                                        ""
                                                    ).includes(
                                                        "PAYMENT"
                                                    ) ? (
                                                        <FiCreditCard />
                                                    ) : (
                                                        <FiFileText />
                                                    )}

                                                    {getReferenceText(
                                                        entry
                                                    )}
                                                </button>

                                                {entry
                                                    ?.payment
                                                    ?.paymentMethod && (
                                                        <small>
                                                            {formatLabel(
                                                                entry
                                                                    .payment
                                                                    .paymentMethod
                                                            )}
                                                        </small>
                                                    )}
                                            </td>

                                            <td>
                                                <span
                                                    className={`customer-ledger-status ${getStatusClass(
                                                        entry
                                                            ?.status
                                                    )}`}
                                                >
                                                    {formatLabel(
                                                        entry
                                                            ?.status ||
                                                        "ACTIVE"
                                                    )}
                                                </span>

                                                {entry
                                                    ?.isReversal && (
                                                        <small className="customer-ledger-reversal-label">
                                                            Reversal
                                                        </small>
                                                    )}
                                            </td>

                                            <td className="customer-ledger-text-right customer-ledger-debit">
                                                {roundMoney(
                                                    entry
                                                        ?.debit
                                                ) >
                                                    0
                                                    ? formatCurrency(
                                                        entry
                                                            ?.debit
                                                    )
                                                    : "-"}
                                            </td>

                                            <td className="customer-ledger-text-right customer-ledger-credit">
                                                {roundMoney(
                                                    entry
                                                        ?.credit
                                                ) >
                                                    0
                                                    ? formatCurrency(
                                                        entry
                                                            ?.credit
                                                    )
                                                    : "-"}
                                            </td>

                                            <td className="customer-ledger-text-right customer-ledger-running-balance">
                                                {formatCurrency(
                                                    isInvoiceLedgerFallback
                                                        ? entries
                                                            .slice(
                                                                0,
                                                                index +
                                                                    1
                                                            )
                                                            .reduce(
                                                                (
                                                                    balance,
                                                                    currentEntry
                                                                ) =>
                                                                    roundMoney(
                                                                        balance +
                                                                        roundMoney(
                                                                            currentEntry
                                                                                ?.debit
                                                                        ) -
                                                                        roundMoney(
                                                                            currentEntry
                                                                                ?.credit
                                                                        )
                                                                    ),
                                                                0
                                                            )
                                                        : (
                                                            entry
                                                                ?.closingBalance ??
                                                            entry
                                                                ?.runningBalance
                                                        )
                                                )}
                                            </td>
                                        </tr>
                                    )
                                )
                            )}
                        </tbody>
                    </table>
                </div>

                <footer className="customer-ledger-footer">
                    <div className="customer-ledger-record-count">
                        Showing{" "}
                        <strong>
                            {entries.length}
                        </strong>{" "}
                        of{" "}
                        <strong>
                            {total}
                        </strong>{" "}
                        entries
                    </div>

                    <div className="customer-ledger-pagination">
                        <label>
                            Rows

                            <select
                                value={
                                    limit
                                }
                                disabled={
                                    loading
                                }
                                onChange={
                                    handleLimitChange
                                }
                            >
                                {PAGE_SIZE_OPTIONS.map(
                                    (
                                        size
                                    ) => (
                                        <option
                                            key={
                                                size
                                            }
                                            value={
                                                size
                                            }
                                        >
                                            {size}
                                        </option>
                                    )
                                )}
                            </select>
                        </label>

                        <button
                            type="button"
                            aria-label="Previous ledger page"
                            disabled={
                                loading ||
                                page <= 1
                            }
                            onClick={
                                handlePreviousPage
                            }
                        >
                            <FiChevronLeft />
                        </button>

                        <span>
                            Page{" "}
                            <strong>
                                {page}
                            </strong>{" "}
                            of{" "}
                            <strong>
                                {totalPages}
                            </strong>
                        </span>

                        <button
                            type="button"
                            aria-label="Next ledger page"
                            disabled={
                                loading ||
                                page >=
                                totalPages
                            }
                            onClick={
                                handleNextPage
                            }
                        >
                            <FiChevronRight />
                        </button>
                    </div>
                </footer>
            </section>
        );
    };

export {
    formatCurrency,
    formatDate,
    formatLabel,
};

export default
    CustomerLedgerStatement;